import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Container, Row, Col, Card, Button, Modal, ToggleButton, ToggleButtonGroup, Spinner } from 'react-bootstrap'
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from 'react-redux'
import { useAuth } from '../../components/auth/useAuth'
import { createUser, getUsers, editUser, enableUser, disableUser, retryProvisionUser } from '../../actions/usersActions'
import { CustomDataTable } from '../../components/design/Datatables/CustomDataTable'
import Swal from 'sweetalert2'
import Loader from '../../components/loaders/Loader'
import UserForm from '../../components/forms/UserForm'
import mapUserToForm from '../../helpers/userFormMapper'

export default function Users() {
    const { t } = useTranslation()
    const location = useLocation()
    const dispatch = useDispatch()
    const {clientauth} = useAuth()

    const {users, resultuser, loading} = useSelector(state=>state.users)

    const [modalShow, setModalShow] = useState(false)
    const [formState, setFormState] = useState({})
    const [isRegistering, setIsRegistering] = useState(false)
    const [isAddingOrEditing, setIsAddingOrEditing] = useState(false)
    const [filterActive, setFilterActive] = useState(true)

    const [page, setPage] = useState(1)
    const [limit, setLimit] = useState(25)
    const [search, setSearch] = useState('')
    const [searchAux, setSearchAux] = useState('')
    const [totalRows, setTotalRows] = useState(0)

    useEffect(() => {
        if(clientauth){
            dispatch(getUsers(page, limit, search))
        }
    }, [dispatch, t, clientauth, location, page, limit, search])

    const openModal = () =>{
        setIsAddingOrEditing('adding')
        setFormState({
            user: '',
            email: '',
            personalEmail: '',
            rol: 'employee',
            apps: {
                microsoft: true,
                odoo: false,
                sesamehr: false,
                iseazy: false
            }
        })
        setModalShow(true)
    }

    const selectUser = (user) => {
        setIsAddingOrEditing('editing')
        setFormState(mapUserToForm(user))
        setModalShow(true)
    }

    const handleInputChange = ({target})=>{
        setFormState({
            ...formState,
            [target.name]: target.value
        })
    }

    const handleCheckboxChange = ({ target }) => {
        if(target.name === "apps"){
            setFormState({
                ...formState,
                apps: target.value
            })
        }else{
            setFormState({
                ...formState,
                [target.name]: target.checked
            })
        }
    }

    const handleSubmit = async(e) => {
        e.preventDefault()
        setIsRegistering(true)

        if(isAddingOrEditing === 'editing'){
            dispatch(editUser(formState, formState._id))
        }else{
            dispatch(createUser(formState))
        }
    }
    const disableEnableUsers = async(e) => {
        e.preventDefault()
        let textQuestion = ''
        formState.active
            ?
                textQuestion = t('users.areyousuredisable')+'<br><p><label className="fs-20 fw-600 pt-5">'+formState?.email+'</label></p>'
            :
                textQuestion = t('users.areyousureenable')+'<br><p><label className="fs-20 fw-600 pt-5">'+formState?.email+'</label></p>'

        Swal.fire({
            showConfirmButton: true,
            showCancelButton: true,
            confirmButtonText: t('global.accept'),
            cancelButtonText: t('global.cancel'),
            icon: 'question',
            html: textQuestion
        }).then((result) => {
            if (result.isConfirmed) {
                if (formState.active) {
                    dispatch(disableUser(formState._id))
                }else{
                    dispatch(enableUser(formState._id))
                }
            }
        })
    }

    const retryProvision = (user, platform) => {
        dispatch(retryProvisionUser(user._id, platform))
    }

    useEffect(() => {
        if(resultuser){
            let resultuserTemp = resultuser
            dispatch({ type: 'USER_RESET_RESULT'})
            switch (resultuserTemp) {
                case 'employeeCreated':
                    Swal.fire({
                        showConfirmButton: true,
                        showCancelButton: false,
                        confirmButtonText: t('global.great'),
                        icon: 'success',
                        text: t('users.addsuccess')
                    }).then(() => {
                        setModalShow(false)
                        setIsAddingOrEditing(false)
                    })
                    break;
                case 'editSuccess':
                    Swal.fire({
                        showConfirmButton: true,
                        showCancelButton: false,
                        confirmButtonText: t('global.great'),
                        icon: 'success',
                        text: t('users.editsuccess')
                    }).then(() => {
                        setModalShow(false)
                        setIsAddingOrEditing(false)
                    })
                    break;
                case 'successEnable':
                    Swal.fire({
                        showConfirmButton: true,
                        showCancelButton: false,
                        confirmButtonText: t('global.great'),
                        icon: 'success',
                        text: t('users.enablesuccess')
                    }).then(() => {
                        setModalShow(false)
                        setIsAddingOrEditing(false)
                    })
                    break;
                case 'successDisable':
                    Swal.fire({
                        showConfirmButton: true,
                        showCancelButton: false,
                        confirmButtonText: t('global.great'),
                        icon: 'success',
                        text: t('users.disablesuccess')
                    }).then(() => {
                        setModalShow(false)
                        setIsAddingOrEditing(false)
                    })
                    break;
                default:
                    break;
            }
        }
    // eslint-disable-next-line
    }, [resultuser])

    const columnsUsers = [
        {
            name: t('users.email'),
            grow: 3,
            cell: user =>
                <div className='cursor' onClick={() => selectUser(user)}>
                    {user.email}
                </div>,
            selector: (user) => user.email,
            sortable: true,
        },{
            name: t('users.type'),
            grow: 2,
            center: true,
            cell: user =>
                <div className='cursor' onClick={() => selectUser(user)}>
                    {t('users.'+user.rol)}
                </div>,
            selector: (user) => user.rol,
            sortable: true,
        },{
            name: t('users.active'),
            grow: 1,
            center: true,
            cell: user =>
                <div>
                    {user.active
                        ?
                            <img src="../images/check.svg" alt="check" width={20}></img>
                        :
                            <img src="../images/times.svg" alt="times" width={20}></img>
                    }
                </div>,
            selector: (user) => user.active,
            sortable: true,
        },{
            name: "Microsoft",
            grow: 1,
            center: true,
            cell: user =>
                user.apps?.microsoft
                    ? <img src="../images/check.svg" width={20} />
                    : <img src="../images/times.svg" width={20} />,
            sortable: false
        },{
        //     name: "Odoo",
        //     grow: 1,
        //     center: true,
        //     cell: user =>
        //         user.apps?.odoo
        //             ? <img src="../images/check.svg" width={20} />
        //             : <img src="../images/times.svg" width={20} />,
        //     sortable: false
        // },{
            name: "SesameHR",
            grow: 1,
            center: true,
            cell: user =>
                user.apps?.sesamehr
                    ? <img src="../images/check.svg" width={20} />
                    : <img src="../images/times.svg" width={20} />,
            sortable: false
        },{
        //     name: "iEazy",
        //     grow: 1,
        //     center: true,
        //     cell: user =>
        //         user.apps?.iseazy
        //             ? <img src="../images/check.svg" width={20} />
        //             : <img src="../images/times.svg" width={20} />,
        //     sortable: false
        // },{
            name: '',
            grow: 1,
            center: true,
            cell: user =>
                <>
                    <div className='cursor me-3' onClick={() => selectUser(user)}>
                        <i className='fas fa-pencil'></i>
                    </div>
                </>,
            sortable: false,
        },{
            name: "",
            grow: 2,
            cell: user => {
                const statuses = user.provisioningStatus || {}
                const failedPlatforms = Object.keys(statuses).filter(
                    p => statuses[p] === "error"
                )
                if (failedPlatforms.length > 0) {
                    return (
                        <>
                            <span className="badge bg-danger me-2">Error</span>

                            {failedPlatforms.map(platform => (
                                <button
                                    key={platform}
                                    className="btn btn-sm btn-outline-primary me-1"
                                    onClick={() => retryProvision(user, platform)}
                                >
                                    {t('users.retry')} {platform}
                                </button>
                            ))}
                        </>
                    )
                }

                // if((statuses.microsoft && statuses.microsoft === "created") && (statuses.sesamehr && statuses.sesamehr === "created")) {
                //     return <span className="badge bg-success">OK</span>
                // }else{
                //     return <span className="badge bg-warning">Pending</span>
                // }
            }
        }
    ]

    const filteredUsers = (users || []).filter(user => user?.active === filterActive)

    return (
        <>
            <Container fluid className='contenido p-5'>
                <Row>
                    <Col xs={12}>
                        <h1>
                            {filterActive
                                ?
                                t('users.usersenabled')
                                :
                                t('users.usersdisabled')
                            }
                        </h1>
                    </Col>
                </Row>
                <Row className='mb-3'>
                    <Col xs={6} className='d-flex justify-content-start'>
                        <ToggleButtonGroup
                            type="radio"
                            name="filterActive"
                            value={filterActive}
                            onChange={setFilterActive}
                        >
                            <ToggleButton id="tbg-active" value={true} variant={filterActive === true ? "primary" : "outline-secondary"}>
                                {t('users.enabled')}
                            </ToggleButton>
                            <ToggleButton id="tbg-inactive" value={false} variant={filterActive === false ? "primary" : "outline-secondary"}>
                                {t('users.disabled')}
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Col>
                    <Col xs={6} className='d-flex justify-content-end'>
                        <Button variant='primary' type="submit" className="my-2 cursor" onClick={() => openModal()}>
                            {t('users.createuser')}
                        </Button>
                    </Col>
                </Row>
                { loading ?
                    <Loader/>
                :
                    <Row>
                        <Col xs={12}>
                            <Card className='card-simple'>
                                <div className="d-flex my-2">
                                    <input
                                        type="text"
                                        className="form-control me-2"
                                        placeholder={t("datatables.buscar")}
                                        value={searchAux || ""}
                                        onChange={e => {
                                            setSearchAux(e.target.value);
                                            setPage(1);
                                        }}
                                        onKeyDown={(e) => {if (e.key === 'Enter') { setSearch(searchAux); setPage(1); }}}
                                    />
                                    <Button onClick={() => setSearch(searchAux)}><i className='fas fa-search'></i></Button>
                                </div>
                            </Card>
                                {loading ?
                                    <div className="d-flex justify-content-center">
                                        <Spinner animation="border" variant="primary" />
                                    </div>
                                :
                                    filteredUsers?.length > 0 ?
                                        <Card className='card-simple mt-2'>
                                            <CustomDataTable
                                                columns={columnsUsers}
                                                data={filteredUsers}
                                                pagination
                                                paginationServer
                                                paginationTotalRows={totalRows}
                                                exportable={false}
                                                printable={false}
                                                searchable={'custom'}
                                                searchText={search}
                                                onChangeRowsPerPage={(newLimit) => {
                                                    setLimit(newLimit)
                                                    setPage(1)
                                                }}
                                                onChangePage={(newPage) => {
                                                    setPage(newPage)
                                                }}
                                            />
                                        </Card>
                                    :
                                        <h4 className='mt-4'>{t('users.thereisnotusers')}</h4>
                                }
                        </Col>
                    </Row>
                }
            </Container>
            <Modal
                show={modalShow}
                onHide={() => setModalShow(false)}
                aria-labelledby="contained-modal-title-vcenter"
                size="lg"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title id="contained-modal-title-vcenter">
                        <h2>{t('users.users')}</h2>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <UserForm
                        state={formState}
                        handleInputChange={handleInputChange}
                        handleCheckboxChange={handleCheckboxChange}
                        submit={handleSubmit}
                        disableEnableUsers={disableEnableUsers}
                        isAddingOrEditing={isAddingOrEditing}
                        isRegistering={isRegistering}
                    />
                </Modal.Body>
            </Modal>
        </>
    )
}